import axios from 'axios';
import config from '../config';
import {
  downloadBlob,
  parseFilenameFromDisposition,
} from '../utils/downloadBlob';
import {
  SupplierStatsDashboardResponse,
  BrandListResponse,
  SupplierStatsListResponse,
  SupplierBrandListResponse,
  BrandProductsResponse,
  BrandCategoriesResponse,
  ProductDetailResponse,
  BrandSortBy,
  SupplierSortBy,
  ProductSortBy,
  SortOrder,
  SupplierStatsGranularity,
} from '../types/supplierStats';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

export const fetchSupplierStatsDashboard = async (
  startDate: string,
  endDate: string,
  granularity: SupplierStatsGranularity = 'color',
): Promise<SupplierStatsDashboardResponse> => {
  const response = await axios.get(
    `${config.API_URL}/supplier-stats/dashboard`,
    {
      params: { startDate, endDate, granularity },
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

export const fetchBrandList = async (params: {
  startDate: string;
  endDate: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: BrandSortBy;
  sortOrder?: SortOrder;
}): Promise<BrandListResponse> => {
  const response = await axios.get(`${config.API_URL}/supplier-stats/brands`, {
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
      search: params.search || undefined,
      page: params.page || 1,
      limit: params.limit || 20,
      sortBy: params.sortBy || 'units',
      sortOrder: params.sortOrder || 'DESC',
    },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const fetchSupplierStatsList = async (params: {
  startDate: string;
  endDate: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: SupplierSortBy;
  sortOrder?: SortOrder;
}): Promise<SupplierStatsListResponse> => {
  const response = await axios.get(
    `${config.API_URL}/supplier-stats/suppliers`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search || undefined,
        page: params.page || 1,
        limit: params.limit || 20,
        sortBy: params.sortBy || 'units',
        sortOrder: params.sortOrder || 'DESC',
      },
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

export const fetchSupplierBrandList = async (
  supplierId: number,
  params: {
    startDate: string;
    endDate: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: BrandSortBy;
    sortOrder?: SortOrder;
  },
): Promise<SupplierBrandListResponse> => {
  const response = await axios.get(
    `${config.API_URL}/supplier-stats/suppliers/${supplierId}/brands`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search || undefined,
        page: params.page || 1,
        limit: params.limit || 20,
        sortBy: params.sortBy || 'units',
        sortOrder: params.sortOrder || 'DESC',
      },
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

export const fetchBrandProductCategories = async (
  brandName: string,
): Promise<BrandCategoriesResponse> => {
  const response = await axios.get(
    `${config.API_URL}/supplier-stats/brands/${encodeURIComponent(brandName)}/categories`,
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

export const fetchBrandProducts = async (
  brandName: string,
  params: {
    startDate: string;
    endDate: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: ProductSortBy;
    sortOrder?: SortOrder;
    granularity?: SupplierStatsGranularity;
    categoryId?: number | null;
  },
): Promise<BrandProductsResponse> => {
  const response = await axios.get(
    `${config.API_URL}/supplier-stats/brands/${encodeURIComponent(brandName)}/products`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search || undefined,
        page: params.page || 1,
        limit: params.limit || 20,
        sortBy: params.sortBy || 'units',
        sortOrder: params.sortOrder || 'DESC',
        granularity: params.granularity || 'color',
        categoryId: params.categoryId || undefined,
      },
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

export const fetchProductDetail = async (
  brandName: string,
  productId: number,
  startDate: string,
  endDate: string,
): Promise<ProductDetailResponse> => {
  const response = await axios.get(
    `${config.API_URL}/supplier-stats/brands/${encodeURIComponent(brandName)}/products/${productId}`,
    {
      params: { startDate, endDate },
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

export type SupplierStatsExportKind =
  | 'dashboard'
  | 'suppliers'
  | 'brands'
  | 'supplier-brands'
  | 'brand-products'
  | 'product-detail';

const exportPathByKind: Record<SupplierStatsExportKind, string> = {
  dashboard: '/supplier-stats/export/dashboard',
  suppliers: '/supplier-stats/export/suppliers',
  brands: '/supplier-stats/export/brands',
  'supplier-brands': '/supplier-stats/export/suppliers',
  'brand-products': '/supplier-stats/export/brands',
  'product-detail': '/supplier-stats/export/brands',
};

export async function downloadSupplierStatsExport(
  kind: SupplierStatsExportKind,
  params: Record<string, string | number | undefined>,
  pathParams?: {
    supplierId?: number;
    brandName?: string;
    productId?: number;
  },
): Promise<void> {
  let path = exportPathByKind[kind];

  if (kind === 'supplier-brands' && pathParams?.supplierId) {
    path = `${path}/${pathParams.supplierId}/brands`;
  } else if (kind === 'brand-products' && pathParams?.brandName) {
    path = `${path}/${encodeURIComponent(pathParams.brandName)}/products`;
  } else if (
    kind === 'product-detail' &&
    pathParams?.brandName &&
    pathParams?.productId
  ) {
    path = `${path}/${encodeURIComponent(pathParams.brandName)}/products/${pathParams.productId}`;
  }

  const response = await axios.get(`${config.API_URL}${path}`, {
    params,
    headers: getAuthHeaders(),
    responseType: 'blob',
  });

  const fallbackName = `leverantorsstatistik_${params.startDate}_${params.endDate}.xlsx`;
  const filename =
    parseFilenameFromDisposition(response.headers['content-disposition']) ||
    fallbackName;

  downloadBlob(response.data, filename);
}

export async function fetchStatistics<T>(
  path: string,
  params: Record<string, string | number>,
  signal: AbortSignal,
): Promise<T> {
  const response = await axios.get(`${config.API_URL}/supplier-stats${path}`, {
    params,
    signal,
    headers: getAuthHeaders(),
  });
  return response.data;
}
